import { usePricingContext } from '@vp/react-pricing-context';
import React from 'react';
import {
    BoundedContent,
    Box,
    Button,
    CarouselSlide,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    H1,
    Link,
    ListPrice,
    Pricing,
    Row,
    ThumbnailsHero,
    ThumbnailsHeroImagesSlidesContainer,
    ThumbnailsHeroThumbnailsSlidesContainer,
    Typography,
    tokens,
} from '@vp/swan';
import { Currency, PriceFromFloat } from '@99designs/design-services-common';
import { __, getCareBriefCreateURL } from '@99designs/i18n';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { ServiceDetailsPageDataQuery } from '../../graphql';
import { ReviewMethod } from '../ReviewMethod';
import { buildRedirectUrl } from './buildRedirectUrl';
import { buildSrcset } from './buildSrcSet';

type Images = ServiceDetailsPageDataQuery['serviceDetailPage']['heroImages'];

export type HeaderProps = {
    heroImages: Images;
    name: string;
    price: ServiceDetailsPageDataQuery['expertServices'][0]['price'];
    sectionTitle: string;
    description: string;
    cta: ServiceDetailsPageDataQuery['serviceDetailPage']['primaryCta'];
    mpvId: string;
    workId: string;
    isDesignLiveEnabled: boolean;
    renderInclusions?: () => JSX.Element;
};

function clickEventData(ctaUrl: string) {
    const pageZone = 'Merchandising Section';
    const label = `Content:${pageZone}`;
    const ctaValue = `Get Started-CTA`;
    const source = window.location.pathname;
    const eventDetail = `${source};${ctaUrl};${pageZone};${ctaValue}`;

    return {
        label,
        eventDetail,
        navigationDetail: ctaValue,
    };
}

export function Header(props: HeaderProps) {
    const [reviewMethod, setReviewMethod] = React.useState('Standard');
    const accessibleTextForDots = [''];
    const inclusions = props.renderInclusions || (() => null);
    const { pageData } = usePage();
    const pricingContext = usePricingContext();

    // Default to the highest price (i.e. taxed) while pricingContext loads
    let price = props.price.listPrice.taxed;
    if (pricingContext && !pricingContext.vatInclusive) {
        price = props.price.listPrice.untaxed;
    }

    const defaultCtaUrl = buildRedirectUrl({
        redirectUrl: getCareBriefCreateURL(),
        mpvId: props.mpvId,
        workId: props.workId,
        multistep: 'allowed',
        requestType: reviewMethod,
    });

    const url = props.cta.targetUrl || defaultCtaUrl;

    return (
        <BoundedContent>
            <GridContainer py={3}>
                <Row sticky>
                    <Column span={7} pl={0}>
                        <ThumbnailsHero accessibleTextForDots={accessibleTextForDots}>
                            <ThumbnailsHeroImagesSlidesContainer>
                                {props.heroImages?.map((image, i) => (
                                    <CarouselSlide
                                        key={`carouselimage1-${i}`}
                                        backgroundColor={'standard'}
                                    >
                                        <FluidImage
                                            srcSet={buildSrcset(image.cloudinaryImage)}
                                            alt={image.altTextOverride || ''}
                                            role="presentation"
                                            style={{
                                                height: 'auto',
                                                width: '100%',
                                                borderRadius: tokens.SwanBaseBorderRadius400,
                                            }}
                                        />
                                    </CarouselSlide>
                                ))}
                            </ThumbnailsHeroImagesSlidesContainer>
                            {props.heroImages?.length > 2 && (
                                <ThumbnailsHeroThumbnailsSlidesContainer>
                                    {props.heroImages?.map((image, i) => (
                                        <CarouselSlide key={`carouselimage2-${i}`}>
                                            <FluidImage
                                                src={
                                                    image.cloudinaryImage.thumbnailCloudinaryUrl ||
                                                    ''
                                                }
                                                alt=""
                                                role="presentation"
                                            />
                                        </CarouselSlide>
                                    ))}
                                </ThumbnailsHeroThumbnailsSlidesContainer>
                            )}
                        </ThumbnailsHero>
                    </Column>
                    <Column span={5} sticky>
                        <FlexBox
                            flexDirection="column"
                            gap={'between-subsections'}
                            mt={7}
                            ml={{ xs: 0, sm: 4 }}
                        >
                            <Box>
                                <Typography>{props.sectionTitle}</Typography>
                                <Box mt={3}>
                                    <H1 mb={0} fontSkin="title-headline">
                                        {props.name}
                                    </H1>
                                </Box>
                                <Box mt={4}>
                                    <Pricing>
                                        <ListPrice fontSkin="title-subsection">
                                            <PriceFromFloat
                                                amount={price}
                                                currency={props.price.currency as Currency}
                                            />
                                        </ListPrice>
                                    </Pricing>
                                </Box>
                            </Box>
                            <Box>
                                <Typography>{props.description}</Typography>
                            </Box>
                            {inclusions()}
                            {props.isDesignLiveEnabled && (
                                <ReviewMethod
                                    reviewMethod={reviewMethod}
                                    setReviewMethod={setReviewMethod}
                                />
                            )}
                            <Button
                                skin="primary"
                                width="full-width"
                                render={(p) => (
                                    <Link
                                        to={url}
                                        className={p.className}
                                        onClick={() => {
                                            sendNavigationEvent({
                                                ...clickEventData(url),
                                                ...pageData,
                                            });
                                        }}
                                    >
                                        {p.children}
                                    </Link>
                                )}
                            >
                                {props.cta.text}
                            </Button>
                        </FlexBox>
                    </Column>
                </Row>
            </GridContainer>
        </BoundedContent>
    );
}
